import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["column", "cardList", "card"]
  static values = { updateUrl: String }

  connect() {
    // Bind event handlers once for reuse
    this.boundDragStart = this.dragStart.bind(this)
    this.boundDragEnd = this.dragEnd.bind(this)
    this.boundDragOver = this.dragOver.bind(this)
    this.boundDragLeave = this.dragLeave.bind(this)
    this.boundDrop = this.drop.bind(this)

    this.setupCardListeners()
    this.setupListListeners()
  }

  disconnect() {
    // Clean up card listeners
    this.cardTargets.forEach(card => {
      card.removeEventListener("dragstart", this.boundDragStart)
      card.removeEventListener("dragend", this.boundDragEnd)
    })

    // Clean up list listeners
    this.cardListTargets.forEach(list => {
      list.removeEventListener("dragover", this.boundDragOver)
      list.removeEventListener("dragleave", this.boundDragLeave)
      list.removeEventListener("drop", this.boundDrop)
    })
  }

  // Handle dynamically added cards via Turbo Streams
  cardTargetConnected(card) {
    card.addEventListener("dragstart", this.boundDragStart)
    card.addEventListener("dragend", this.boundDragEnd)
  }

  cardTargetDisconnected(card) {
    card.removeEventListener("dragstart", this.boundDragStart)
    card.removeEventListener("dragend", this.boundDragEnd)
  }

  // Handle dynamically added card lists
  cardListTargetConnected(list) {
    list.addEventListener("dragover", this.boundDragOver)
    list.addEventListener("dragleave", this.boundDragLeave)
    list.addEventListener("drop", this.boundDrop)
  }

  cardListTargetDisconnected(list) {
    list.removeEventListener("dragover", this.boundDragOver)
    list.removeEventListener("dragleave", this.boundDragLeave)
    list.removeEventListener("drop", this.boundDrop)
  }

  setupCardListeners() {
    this.cardTargets.forEach(card => {
      card.addEventListener("dragstart", this.boundDragStart)
      card.addEventListener("dragend", this.boundDragEnd)
    })
  }

  setupListListeners() {
    this.cardListTargets.forEach(list => {
      list.addEventListener("dragover", this.boundDragOver)
      list.addEventListener("dragleave", this.boundDragLeave)
      list.addEventListener("drop", this.boundDrop)
    })
  }

  dragStart(event) {
    event.dataTransfer.setData("text/plain", event.target.dataset.cardId)
    event.dataTransfer.effectAllowed = "move"
    event.target.classList.add("opacity-50", "rotate-2")

    // Add a slight delay before adding the dragging class for visual feedback
    setTimeout(() => {
      event.target.classList.add("dragging")
    }, 0)
  }

  dragEnd(event) {
    event.target.classList.remove("opacity-50", "rotate-2", "dragging")
  }

  dragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"

    const cardList = event.target.closest("[data-kanban-board-target='cardList']")
    if (cardList) {
      cardList.classList.add("bg-blue-50", "ring-2", "ring-blue-300", "ring-inset")
    }
  }

  dragLeave(event) {
    const cardList = event.target.closest("[data-kanban-board-target='cardList']")
    if (cardList && !cardList.contains(event.relatedTarget)) {
      cardList.classList.remove("bg-blue-50", "ring-2", "ring-blue-300", "ring-inset")
    }
  }

  drop(event) {
    event.preventDefault()

    const cardId = event.dataTransfer.getData("text/plain")
    const cardList = event.target.closest("[data-kanban-board-target='cardList']")

    if (!cardList) return

    cardList.classList.remove("bg-blue-50", "ring-2", "ring-blue-300", "ring-inset")

    const columnId = cardList.dataset.columnId
    const card = this.cardTargets.find(c => c.dataset.cardId === cardId)

    if (card) {
      // Move the card in the DOM
      cardList.appendChild(card)

      // Calculate new position
      const position = this.getCardPosition(card, cardList)

      // Send update to server
      this.updateCardPosition(cardId, columnId, position)
    }
  }

  getCardPosition(card, cardList) {
    const cards = Array.from(cardList.querySelectorAll("[data-kanban-board-target='card']"))
    return cards.indexOf(card)
  }

  async updateCardPosition(cardId, columnId, position) {
    const url = this.updateUrlValue.replace("CARD_ID", cardId)
    const csrfToken = document.querySelector("[name='csrf-token']")?.content

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          column_id: columnId,
          position: position
        })
      })

      if (!response.ok) {
        console.error("Failed to update card position:", response.status)
        // Optionally reload to sync state
        // window.location.reload()
      }
    } catch (error) {
      console.error("Error updating card position:", error)
    }
  }
}
